"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Button, Modal, ModalHeader, ModalBody, FormGroup, Label, Input, Spinner, FormFeedback } from "reactstrap"
import Swal from "sweetalert2"
import ExhibitorTypeService from "../../../Service/ExhibitorTypeService"
import { toast } from "react-toastify"
import type { ExhibitorType } from "./types"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
  initialData: ExhibitorType | null
}

const ExhibitorTypeModal = ({ isOpen, toggle, onSuccess, initialData }: Props) => {
  const [form, setForm] = useState<Omit<ExhibitorType, "id">>({
    name: "",
    require_tin: true,
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<{
    name?: string
  }>({})

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name,
        require_tin: initialData.require_tin || true,
      })
    } else {
      setForm({ name: "", require_tin: true })
    }
    // Clear errors when modal opens/closes or data changes
    setErrors({})
  }, [initialData, isOpen])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    })

    // Clear error for this field when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors({
        ...errors,
        [name]: undefined,
      })
    }
  }

  const validateForm = (): boolean => {
    const newErrors: { name?: string } = {}

    // Name validation
    if (!form.name.trim()) {
      newErrors.name = "Name is required"
    } else if (form.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters long"
    } else if (form.name.trim().length > 100) {
      newErrors.name = "Name must not exceed 100 characters"
    } else if (!/^[a-zA-Z0-9\s\-_&().,]+$/.test(form.name.trim())) {
      newErrors.name = "Name contains invalid characters"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const resetForm = () => {
    setForm({ name: "", require_tin: true })
    setErrors({})
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fix the validation errors")
      return
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to ${initialData ? "update" : "create"} this Exhibitor Type?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${initialData ? "update" : "create"} it!`,
      cancelButtonText: "Cancel",
    })

    if (!result.isConfirmed) {
      return
    }

    try {
      setLoading(true)

      const formData = {
        name: form.name.trim(),
        require_tin: form.require_tin,
      }

      if (initialData) {
        await ExhibitorTypeService.update(initialData.id, formData)

        await Swal.fire({
          title: "Updated!",
          text: "Exhibitor Type has been updated successfully.",
          icon: "success",
          confirmButtonColor: "#28a745",
        })
      } else {
        await ExhibitorTypeService.create(formData)

        await Swal.fire({
          title: "Created!",
          text: "Exhibitor Type has been created successfully.",
          icon: "success",
          confirmButtonColor: "#28a745",
        })

        // Reset form after successful creation
        resetForm()
      }

      onSuccess() // refetch
      toggle()
    } catch (error: any) {
      console.error("Exhibitor Type operation error:", error)

      let errorMessage = "Something went wrong. Please try again."

      // Handle specific error cases
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.data?.name) {
        errorMessage = Array.isArray(error.response.data.name) ? error.response.data.name[0] : error.response.data.name
      } else if (error.response?.data?.non_field_errors) {
        errorMessage = Array.isArray(error.response.data.non_field_errors) 
          ? error.response.data.non_field_errors[0] 
          : error.response.data.non_field_errors
      }

      await Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        confirmButtonColor: "#dc3545",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleModalToggle = () => {
    if (!initialData) {
      resetForm()
    }
    toggle()
  }

  return (
    <Modal isOpen={isOpen} toggle={handleModalToggle}>
      <ModalHeader toggle={handleModalToggle}>
        {initialData ? "Edit" : "Add"} Exhibitor Type
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="name">
            Name <span className="text-danger">*</span>
          </Label>
          <Input
            name="name"
            value={form.name}
            onChange={handleChange}
            invalid={!!errors.name}
            placeholder="Enter exhibitor type name"
            maxLength={100}
          />
          {errors.name && <FormFeedback>{errors.name}</FormFeedback>}
          <small className="text-muted">{form.name.length}/100 characters</small>
        </FormGroup>

        <FormGroup check className="form-switch mt-3">
          <Input
            type="checkbox"
            name="require_tin"
            checked={form.require_tin}
            onChange={handleChange}
          />
          <Label check className="ms-2">
            Require TIN (Tax Identification Number)
          </Label>
          <small className="text-muted d-block mt-1">
            {form.require_tin 
              ? "This exhibitor type requires TIN verification" 
              : "TIN verification is optional for this type"
            }
          </small>
        </FormGroup>

        <div className="text-end mt-4">
          <Button color="secondary" onClick={handleModalToggle} className="me-2" disabled={loading}>
            Cancel
          </Button>
          <Button 
            color="primary" 
            onClick={handleSubmit} 
            disabled={loading || !form.name.trim()}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-1" />
                {initialData ? "Updating..." : "Creating..."}
              </>
            ) : initialData ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default ExhibitorTypeModal